import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

interface SeoData {
  title: string;
  description: string;
  keywords: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://settlespace.in';

const KEYWORDS =
  'rental properties Navi Mumbai, rental homes Kharghar, flats for rent Kharghar, rental properties CBD Belapur, flats for rent Seawoods, rental homes Nerul, property rental, tenant support, property owner support, home rental, real estate, Navi Mumbai real estate';

// Unified routing: metadata is keyed by the effective section token, which can
// arrive either as a ?section= query value or as a clean slash proxy path.
// Empty string = the home view.
const seoBySection: Record<string, SeoData> = {
  '': {
    title: 'SettleSpace - Home Rental Experts, Navi Mumbai',
    description:
      'We help tenants and owners through a safe, transparent and hassle-free rental process across Kharghar, Navi Mumbai and nearby areas.',
    keywords: KEYWORDS,
  },
  services: {
    title: 'Our Services | SettleSpace',
    description:
      'A clear list of Benefits that are concisely mentioned for Tenants & Owners.',
    keywords: KEYWORDS,
  },
  testimonials: {
    title: 'Trusted by Tenants & Owners | SettleSpace',
    description:
      "Read genuine experiences from people we've worked with that are now happy and well settled.",
    keywords: KEYWORDS,
  },
  contact: {
    title: 'Contact Us | SettleSpace',
    description:
      'Get in touch with us on WhatsApp, Phone, e-Mail or fill out an Inquiry form.',
    keywords: KEYWORDS,
  },
  help: {
    title: 'Help Center for Tenants & Owners | SettleSpace',
    description:
      "FAQ's & Queries answered in regards to the rental process and our services.",
    keywords: KEYWORDS,
  },
  privacy: {
    title: 'Privacy Policy | SettleSpace',
    description:
      'How we collect, use, store and protect personal information for rental services in Navi Mumbai.',
    keywords: KEYWORDS,
  },
  terms: {
    title: 'Terms of Service | SettleSpace',
    description:
      "The official brief Legalities of our company and it's services offered.",
    keywords: KEYWORDS,
  },
};

// Clean slash proxy folder paths → their section token, so metadata resolves
// correctly whether traffic arrives on the slash path or the query string.
const PATH_TO_SECTION: Record<string, string> = {
  '/services': 'services',
  '/testimonials': 'testimonials',
  '/contact': 'contact',
  '/help-center': 'help',
  '/privacy-policy': 'privacy',
  '/terms-of-service': 'terms',
};

// Section token → its canonical clean slash path (used for canonical URLs).
const SECTION_TO_PATH: Record<string, string> = {
  services: '/services',
  testimonials: '/testimonials',
  contact: '/contact',
  help: '/help-center',
  privacy: '/privacy-policy',
  terms: '/terms-of-service',
};

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector =
    attr === 'name' ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export default function SeoManager() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Resolve the effective section from the query token first, then fall back
  // to a matching clean slash proxy path.
  const querySection = searchParams.get('section') ?? '';
  const cleanPath = location.pathname.replace(/\/+$/, '') || '/';
  const pathSection = PATH_TO_SECTION[cleanPath] ?? '';
  const section = querySection || pathSection;

  useEffect(() => {
    const data = seoBySection[section] ?? seoBySection[''];

    // Resolve the REAL active path so og:url / twitter:url never fall back to a
    // hardcoded root. Prefer the live window pathname; when the current view was
    // reached via a ?section= token (e.g. /?section=services), map it to its
    // canonical slash path so social scrapers bind to the individual section.
    const windowPath =
      typeof window !== 'undefined'
        ? window.location.pathname.replace(/\/+$/, '')
        : '';
    const livePath = windowPath || '/';
    const activePath =
      livePath !== '/'
        ? livePath
        : section && SECTION_TO_PATH[section]
          ? SECTION_TO_PATH[section]
          : '/';
    const activeUrl = `${SITE_URL}${activePath === '/' ? '/' : activePath}`;

    document.title = data.title;

    upsertMeta('name', 'description', data.description);
    upsertMeta('name', 'keywords', data.keywords);
    upsertCanonical(activeUrl);

    // Open Graph
    upsertMeta('property', 'og:title', data.title);
    upsertMeta('property', 'og:description', data.description);
    upsertMeta('property', 'og:url', activeUrl);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'SettleSpace');

    // Twitter Card
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', data.title);
    upsertMeta('name', 'twitter:description', data.description);
    upsertMeta('name', 'twitter:url', activeUrl);
  }, [location.search, location.pathname, section]);

  return null;
}